import cx from "classnames";
import { Icon } from "common/Icon";
import { CTA } from "common/styleguide/CTA";
import { RadioButtonGroup } from "common/styleguide/RadioGroup";
import {
  messages as trouteSurfacesMessages,
  PAVED,
  UNPAVED,
} from "features/troute_show/TRouteShowPage/TRouteSidePanel/TRouteSurfaces";
import { OrderedMap } from "immutable";
import { commonCTAs, useFormatMessage } from "lib/localization";
import React, { useEffect, useRef, useState } from "react";
import { defineMessages, FormattedMessage as T } from "react-intl";
import { colors } from "../ClickActions";
import { messages as selectionToolsMessages } from "../SelectionTools";
import styles from "./SelectionToolHelper.scss";

const R = window.Routes;

const messages = defineMessages({
  deleteSelection: {
    id: "SelectionToolHelper.deleteSelection",
    defaultMessage: "Delete Selection",
  },
  flattenElevation: {
    id: "SelectionToolHelper.flattenElevation",
    defaultMessage: "Flatten Elevation",
  },
  changeColor: {
    id: "SelectionToolHelper.changeColor",
    defaultMessage: "Change Color",
  },
  removeControlPoints: {
    id: "SelectionToolHelper.removeControlPoints",
    defaultMessage: "Remove Control Points",
  },
  excludeFromMetrics: {
    id: "SelectionToolHelper.excludeFromMetrics",
    defaultMessage: "Exclude from Metrics",
  },
  crop: {
    id: "SelectionToolHelper.crop",
    defaultMessage: "Crop",
  },
  chooseASurface: {
    id: "SelectionToolHelper.chooseASurface",
    defaultMessage: "Choose a surface type",
  },
});
messages.changeSurface = selectionToolsMessages.changeSurface;

const surfaceOpts = (fm) =>
  OrderedMap({
    [PAVED]: fm(trouteSurfacesMessages.paved),
    [UNPAVED]: fm(trouteSurfacesMessages.unpaved),
  });

export const RightSidebarTool = ({ title, onRequestClose, children }) => (
  <div className={styles.SelectionToolHelper}>
    <div className={styles.controls} onClick={onRequestClose}>
      <Icon i="ios-arrow-back" size={24} />
      <T id="SelectionToolHelper.returnToPlanner" defaultMessage="Return to the Route Planner" />
    </div>
    <div className={styles.header}>{title}</div>
    <div className={styles.helpText}>{children}</div>
  </div>
);

export const SelectionToolHelper = ({ close, action, performAction }) => {
  const fm = useFormatMessage();

  const [start, setStart] = useState();
  const [end, setEnd] = useState();

  // the selectPoint closure needs a stable reference to check if there's a start
  const startRef = useRef();
  const endRef = useRef();
  startRef.current = start;
  endRef.current = end;
  const selectPoint = ({ closestI }) => {
    (!startRef.current ? setStart : setEnd)(closestI);
  };
  useEffect(() => {
    R.sampleGraph.bind("pointselected", selectPoint);
    R.activeMap.selectionToolHelper = { selectPoint };
    R.activeMap.disableClicks();
    return () => {
      R.sampleGraph.unbind("pointselected", selectPoint);
      delete R.activeMap.selectionToolHelper;
      R.activeMap.enableClicks();
    };
  }, []);
  const startPointMarker = useRef();
  useEffect(() => {
    if (start && start !== -1 && !end) {
      startPointMarker.current = new window.google.maps.Marker({
        position: R.sampleGraph.data()[start].point,
        map: R.activeMap.gmap,
        icon: "/images/location-icon.png",
        origin: new window.google.maps.Point(14, 37),
      });
    }
    return () => startPointMarker.current && startPointMarker.current.setMap(null);
  }, [start, end]);

  useEffect(() => {
    if (start && end) {
      if (start !== -1) {
        const slice = R.sampleGraph.dataSlice(end > start ? start : end, Math.abs(end - start) + 1);
        R.sampleGraph.selectSegment(slice);
      }
      return () => {
        R.sampleGraph.segmentSelector && R.sampleGraph.segmentSelector.unload();
      };
    }
  }, [start, end]);

  const onSelect = () => {
    if (!startRef.current) setStart(-1);
    if (!endRef.current) setEnd(-1);
  };
  useEffect(() => {
    R.sampleGraph.bindTo(R.sampleGraph, "dataslice:select", onSelect);
    return () => R.sampleGraph.unbindFrom(R.sampleGraph, "dataslice:select", onSelect);
  }, []);
  // A selection is already loaded (this happens with the Change Color tool)
  if (R.sampleGraph.segmentSelector) onSelect();

  // Only relevant for change color tool
  const [selectedColor, setSelectedColor] = useState(colors[0]);
  // Only relevant for change surfaces tool
  const [selectedSurface, setSelectedSurface] = useState(PAVED);

  return (
    <RightSidebarTool onRequestClose={close} title={fm(messages[action])}>
      {!start && (
        <>
          <T
            id="SelectionToolHelper.instructionStart"
            defaultMessage="Choose the <strong>start of your selection</strong> by clicking directly on the route, or on the elevation profile."
            values={{ strong: (...chunks) => <strong>{chunks}</strong> }}
          />
          <CTA secondary onClick={close}>
            {fm(commonCTAs.cancel)}
          </CTA>
        </>
      )}
      {start && !end && (
        <>
          <T
            id="SelectionToolHelper.instructionEnd"
            defaultMessage="Choose the <strong>end of your selection</strong> by clicking directly on the route, or on the elevation profile."
            values={{ strong: (...chunks) => <strong>{chunks}</strong> }}
          />
          <CTA secondary onClick={close}>
            {fm(commonCTAs.cancel)}
          </CTA>
        </>
      )}
      {start && end && (
        <>
          <T
            id="SelectionToolHelper.instructionAdjust"
            defaultMessage="You can adjust your selection by dragging the window on the elevation graph."
          />
          {action === "changeColor" && <ColorPicker selectedColor={selectedColor} onColorChange={setSelectedColor} />}
          {action === "changeSurface" && (
            <SurfacePicker selectedSurface={selectedSurface} onSurfaceChange={setSelectedSurface} />
          )}
          <CTA
            onClick={() => {
              performAction(action === "changeColor" ? { color: selectedColor } : { surface: selectedSurface });
              close();
            }}
          >
            {fm(messages[action])}
          </CTA>
          <CTA secondary onClick={close}>
            {fm(commonCTAs.cancel)}
          </CTA>
        </>
      )}
    </RightSidebarTool>
  );
};

const ColorPicker = ({ selectedColor, onColorChange }) => {
  return (
    <>
      <div style={{ marginTop: "15px" }}>
        <T id="ColorPicker.chooseAColor" defaultMessage="Choose a color" />:
      </div>
      <div className={styles.swatches}>
        {colors.map((color) => (
          <div
            key={color}
            onClick={() => onColorChange(color)}
            className={cx(styles.swatch, color === selectedColor && styles.selected)}
          >
            <div
              style={{
                backgroundColor: color,
                border: color === "#FFFF00" && "1px solid #BDBDBD",
              }}
            />
          </div>
        ))}
      </div>
    </>
  );
};

const SurfacePicker = ({ selectedSurface, onSurfaceChange }) => {
  const fm = useFormatMessage();
  return (
    <>
      <div style={{ marginTop: "15px", marginBottom: "15px" }}>{fm(messages.chooseASurface)}:</div>
      <RadioButtonGroup
        name="climbselectorbuttons"
        options={surfaceOpts(fm)}
        selected={`${selectedSurface}`}
        onChange={onSurfaceChange}
      />
    </>
  );
};
